import React, { useState, useEffect, useCallback } from 'react';
import { 
  FolderOpen, 
  FileText, 
  BookOpen, 
  Database, 
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  Loader2,
  Search,
  Plus,
  ArrowRight
} from 'lucide-react';
import { useGlobalContext } from '../contexts/GlobalContextProvider';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'project', label: 'Project', icon: FolderOpen, color: '#9333EA' },
  { id: 'instruction', label: 'Instructions', icon: FileText, color: '#3B82F6' },
  { id: 'glossary', label: 'Glossary', icon: BookOpen, color: '#10B981' },
  { id: 'tm', label: 'Translation Memory', icon: Database, color: '#F59E0B' },
];

// Pre-built instructions for selection
const PREBUILT_INSTRUCTIONS = [
  { id: 'formal-tone', name: 'Formal Tone', description: 'Maintain formal tone throughout' },
  { id: 'preserve-brands', name: 'Preserve Brands', description: 'Preserve brand names and product names' },
  { id: 'keep-technical', name: 'Keep Technical', description: 'Keep technical terms in English' },
  { id: 'gender-neutral', name: 'Gender Neutral', description: 'Use gender-neutral language' },
  { id: 'adapt-idioms', name: 'Adapt Idioms', description: 'Adapt idioms and cultural references' },
  { id: 'consistent-terminology', name: 'Consistent Terms', description: 'Maintain consistent terminology' },
  { id: 'preserve-formatting', name: 'Preserve Format', description: 'Preserve formatting and structure' },
  { id: 'industry-specific', name: 'Industry Terms', description: 'Use industry-specific terminology' },
  { id: 'keep-numbers', name: 'Keep Numbers', description: 'Keep numbers and dates in original format' },
];

const ContextSelectorModal = ({ isOpen, onClose, variant = 'default' }) => {
  const {
    selectedProject,
    selectedInstruction,
    selectedGlossary,
    selectedTM,
    setSelectedProject,
    setSelectedInstruction,
    setSelectedGlossary,
    setSelectedTM,
    selectProject,
  } = useGlobalContext();

  const { isDarkMode } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data states
  const [projects, setProjects] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [glossaries, setGlossaries] = useState([]);
  const [tms, setTms] = useState([]);
  
  // Temporary selections (not committed until Apply)
  const [tempProject, setTempProject] = useState(selectedProject);
  const [tempInstruction, setTempInstruction] = useState(selectedInstruction);
  const [tempGlossary, setTempGlossary] = useState(selectedGlossary);
  const [tempTM, setTempTM] = useState(selectedTM);

  // Reset temp selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempProject(selectedProject);
      setTempInstruction(selectedInstruction);
      setTempGlossary(selectedGlossary);
      setTempTM(selectedTM);
      setCurrentStep(0);
      setSearchQuery('');
    }
  }, [isOpen, selectedProject, selectedInstruction, selectedGlossary, selectedTM]);

  // Fetch projects on mount
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  // Fetch glossaries and TMs when project changes
  useEffect(() => {
    if (tempProject) {
      fetchProjectResources(tempProject.id || tempProject.project_id);
    }
  }, [tempProject]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data || []);
      
      // Build instructions from projects
      const projectInstructions = (response.data || [])
        .filter(p => p.instruction_name || p.custom_instructions)
        .map(p => ({
          id: p.id || p.project_id,
          name: p.instruction_name || `${p.name} Instructions`,
          description: p.custom_instructions?.slice(0, 100) + '...' || '',
          projectId: p.id || p.project_id,
          type: 'project'
        }));
      
      // Also fetch user's instruction sets
      try {
        const instrRes = await axios.get(`${API_URL}/api/instructions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const instrSets = (instrRes.data || []).map(i => ({
          id: i.id,
          name: i.name,
          description: i.instructions?.slice(0, 100) + '...' || '',
          content: i.instructions,
          type: 'instruction_set'
        }));
        setInstructions([...PREBUILT_INSTRUCTIONS, ...instrSets, ...projectInstructions]);
      } catch (e) {
        setInstructions([...PREBUILT_INSTRUCTIONS, ...projectInstructions]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectResources = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch glossary
      const glossaryRes = await axios.get(`${API_URL}/api/projects/${projectId}/glossary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const glossaryTerms = glossaryRes.data?.glossary?.terms || [];
      if (glossaryTerms.length > 0) {
        setGlossaries([{
          id: projectId,
          name: `${tempProject?.name || 'Project'} Glossary`,
          termCount: glossaryTerms.length,
          projectId: projectId
        }]);
      } else {
        setGlossaries([]);
      }

      // Fetch TM stats
      const tmRes = await axios.get(`${API_URL}/api/projects/${projectId}/tm/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tmStats = tmRes.data;
      if (tmStats?.project_not_found) {
        // Project deleted — clear stale project from context so it stops retrying
        setSelectedProject(null);
        setTms([]);
      } else if (tmStats?.total_entries > 0) {
        setTms([{
          id: projectId,
          name: `${tempProject?.name || 'Project'} TM`,
          entryCount: tmStats.total_entries,
          projectId: projectId
        }]);
      } else {
        setTms([]);
      }
    } catch (error) {
      console.error('Failed to fetch project resources:', error);
      setGlossaries([]);
      setTms([]);
    }
  };

  const handleApply = () => {
    setSelectedProject(tempProject);
    setSelectedInstruction(tempInstruction);
    setSelectedGlossary(tempGlossary);
    setSelectedTM(tempTM);
    onClose();
  };

  const handleSkipStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const filteredItems = useCallback(() => {
    const query = searchQuery.toLowerCase();
    switch (STEPS[currentStep].id) {
      case 'project':
        return projects.filter(p => p.name?.toLowerCase().includes(query));
      case 'instruction':
        return instructions.filter(i => 
          i.name?.toLowerCase().includes(query) || 
          i.description?.toLowerCase().includes(query)
        );
      case 'glossary':
        return glossaries.filter(g => g.name?.toLowerCase().includes(query));
      case 'tm':
        return tms.filter(t => t.name?.toLowerCase().includes(query));
      default:
        return [];
    }
  }, [currentStep, searchQuery, projects, instructions, glossaries, tms]);

  const handleSelectItem = (item) => {
    switch (STEPS[currentStep].id) {
      case 'project':
        setTempProject(item);
        // Auto-advance to next step
        setTimeout(() => setCurrentStep(1), 200);
        break;
      case 'instruction':
        setTempInstruction(item);
        setTimeout(() => setCurrentStep(2), 200);
        break;
      case 'glossary':
        setTempGlossary(item);
        setTimeout(() => setCurrentStep(3), 200);
        break;
      case 'tm':
        setTempTM(item);
        break;
    }
  };

  const isItemSelected = (item) => {
    switch (STEPS[currentStep].id) {
      case 'project':
        return tempProject?.id === item.id || tempProject?.project_id === item.id;
      case 'instruction':
        return tempInstruction?.id === item.id;
      case 'glossary':
        return tempGlossary?.id === item.id;
      case 'tm':
        return tempTM?.id === item.id;
      default:
        return false;
    }
  };

  const currentSelection = () => {
    switch (STEPS[currentStep].id) {
      case 'project': return tempProject;
      case 'instruction': return tempInstruction;
      case 'glossary': return tempGlossary;
      case 'tm': return tempTM;
      default: return null;
    }
  };

  if (!isOpen) return null;

  const isApple = variant === 'apple';
  const step = STEPS[currentStep];
  const StepIcon = step.icon;

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="context-selector-modal"
    >
      <div
        style={{
          width: '100%',
          maxWidth: '640px',
          maxHeight: '85vh',
          background: isDarkMode ? '#25262b' : (isApple ? 'var(--apple-surface-elevated, #fff)' : '#fff'),
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          background: isDarkMode ? '#2c2e33' : `linear-gradient(135deg, ${step.color}10 0%, transparent 100%)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: '20px', 
              fontWeight: '700', 
              color: isApple ? 'var(--apple-text-primary, #1d1d1f)' : '#111827',
              letterSpacing: '-0.5px'
            }}>
              Set Translation Context
            </h2>
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0, 0, 0, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0, 0, 0, 0.05)'}
            >
              <X size={18} color="#6B7280" />
            </button>
          </div>

          {/* Step indicators */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep || (idx === currentStep && currentSelection());
              
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => setCurrentStep(idx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      borderRadius: '12px',
                      border: 'none',
                      background: isActive ? s.color : isCompleted ? `${s.color}15` : 'rgba(0, 0, 0, 0.04)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Icon size={16} color={isActive ? '#fff' : isCompleted ? s.color : '#9CA3AF'} />
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: isActive ? '#fff' : isCompleted ? s.color : '#9CA3AF',
                    }}>
                      {s.label}
                    </span>
                    {isCompleted && !isActive && (
                      <Check size={14} color={s.color} />
                    )}
                  </button>
                  {idx < STEPS.length - 1 && (
                    <ArrowRight size={14} color="#D1D5DB" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Current step header */}
          <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(0, 0, 0, 0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}CC 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <StepIcon size={20} color="white" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Select {step.label}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
                  {step.id === 'project' && 'Choose a project to use its settings and resources'}
                  {step.id === 'instruction' && 'Select translation instructions to guide the engine'}
                  {step.id === 'glossary' && 'Apply glossary terms for consistent terminology'}
                  {step.id === 'tm' && 'Leverage translation memory for faster translations'}
                </p>
              </div>
            </div>

            {/* Search */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: 'rgba(0, 0, 0, 0.03)',
              borderRadius: '12px',
            }}>
              <Search size={18} color="#9CA3AF" />
              <input
                type="text"
                placeholder={`Search ${step.label.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: '14px',
                  outline: 'none',
                  color: '#111827',
                }}
              />
            </div>
          </div>

          {/* Items list */}
          <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px' }}>
                <Loader2 size={32} color={step.color} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : filteredItems().length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '48px 24px',
                color: '#6B7280',
              }}>
                <StepIcon size={48} color="#D1D5DB" style={{ margin: '0 auto 16px' }} />
                <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>
                  {step.id === 'project' && 'No projects found'}
                  {step.id === 'instruction' && 'No instructions available'}
                  {step.id === 'glossary' && 'No glossaries found. Select a project with glossary terms.'}
                  {step.id === 'tm' && 'No translation memories found. Select a project with TM entries.'}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#9CA3AF' }}>
                  {step.id === 'project' && 'Create a project to get started'}
                  {step.id === 'glossary' && 'You can skip this step'}
                  {step.id === 'tm' && 'You can skip this step'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredItems().map((item, idx) => {
                  const selected = isItemSelected(item);
                  return (
                    <button
                      key={item.id || idx}
                      onClick={() => handleSelectItem(item)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        padding: '14px 16px',
                        background: selected ? `${step.color}10` : (isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0, 0, 0, 0.02)'),
                        border: selected ? `2px solid ${step.color}` : '2px solid transparent',
                        borderRadius: '14px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseOver={(e) => !selected && (e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0, 0, 0, 0.04)')}
                      onMouseOut={(e) => !selected && (e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0, 0, 0, 0.02)')}
                      data-testid={`context-item-${item.id || idx}`}
                    >
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: selected ? step.color : `${step.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <StepIcon size={20} color={selected ? '#fff' : step.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '15px', 
                          fontWeight: '600', 
                          color: selected ? step.color : '#111827',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {item.name}
                        </p>
                        {item.description && (
                          <p style={{ 
                            margin: '2px 0 0', 
                            fontSize: '13px', 
                            color: '#6B7280',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {item.description}
                          </p>
                        )}
                        {(item.source_lang || item.target_lang) && (
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                            {item.source_lang} → {item.target_lang}
                          </p>
                        )}
                        {item.termCount !== undefined && (
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                            {item.termCount} terms
                          </p>
                        )}
                        {item.entryCount !== undefined && (
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                            {item.entryCount} entries
                          </p>
                        )}
                      </div>
                      {selected && (
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: step.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Check size={14} color="white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0, 0, 0, 0.02)',
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  background: 'transparent',
                  border: '1px solid rgba(0, 0, 0, 0.15)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  transition: 'all 0.15s ease',
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <ChevronLeft size={16} />
                Back
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {currentStep < STEPS.length - 1 && (
              <button
                onClick={handleSkipStep}
                style={{
                  padding: '10px 16px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6B7280',
                  transition: 'all 0.15s ease',
                }}
                onMouseOver={(e) => e.currentTarget.style.color = '#374151'}
                onMouseOut={(e) => e.currentTarget.style.color = '#6B7280'}
              >
                Skip
              </button>
            )}
            
            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!currentSelection()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  background: currentSelection() ? `linear-gradient(135deg, ${step.color} 0%, ${step.color}DD 100%)` : '#E5E7EB',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: currentSelection() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: currentSelection() ? 'white' : '#9CA3AF',
                  transition: 'all 0.15s ease',
                }}
              >
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleApply}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #9333EA 0%, #3B82F6 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  transition: 'all 0.15s ease',
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                data-testid="apply-context-btn"
              >
                <Check size={16} />
                Apply Context
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ContextSelectorModal;
